import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from "recharts";
import { getApiBaseUrl } from "../../../config/api";
import { getMalawiGrade, getClassificationColor, gpaBandLetter, exportCsv } from "../../../utils/gradingUtils";
import { allowedReportPeriods } from "../../../utils/reportPeriodUtils";
import { KpiCard as SharedKpiCard, ResponsiveTableWrap } from "../examRelated/resultsUi";

const SEMESTERS = ["Semester 1", "Semester 2"];

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];

const ClassResults = () => {
    const { id, classId: classIdParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const recordsMode = location.pathname.includes("/Admin/exams/records");
    const effectiveClassId = recordsMode ? classIdParam : id;

    const [classData, setClassData] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [primaryResults, setPrimaryResults] = useState([]);
    const [universityResults, setUniversityResults] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");

    const currentYear = new Date().getFullYear();
    const defaultAcademicYear = `${currentYear}/${currentYear + 1}`;
    const academicYearOptions = useMemo(() => {
        const set = new Set([`${currentYear - 1}/${currentYear}`, `${currentYear}/${currentYear + 1}`, `${currentYear + 1}/${currentYear + 2}`]);
        if (classData?.academicYear) {
            set.add(classData.academicYear);
        }
        return Array.from(set);
    }, [classData, currentYear]);
    const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
    const [semester, setSemester] = useState("Semester 1");
    const [recordTerm, setRecordTerm] = useState("Term 1");
    const [recordYear, setRecordYear] = useState(String(new Date().getFullYear()));
    const [recordPeriod, setRecordPeriod] = useState("End of term");
    const [publishedPeriods, setPublishedPeriods] = useState([]);

    const educationLevel = classData?.educationLevel || "Secondary";
    const isUniversity = educationLevel === "University";

    useEffect(() => {
        let cancelled = false;
        const loadClass = async () => {
            setInitialLoading(true);
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Sclass/${effectiveClassId}`);
                if (cancelled) return;
                setClassData(data);
                if (data?.academicYear) {
                    setAcademicYear(data.academicYear);
                }
                if (data?.semester && SEMESTERS.includes(data.semester)) {
                    setSemester(data.semester);
                }
                setErrorMessage("");
            } catch (err) {
                if (!cancelled) {
                    setErrorMessage(err.response?.data?.message || "Could not load class details");
                }
            } finally {
                if (!cancelled) setInitialLoading(false);
            }
        };
        loadClass();
        return () => {
            cancelled = true;
        };
    }, [effectiveClassId]);

    useEffect(() => {
        if (!recordsMode || !effectiveClassId) {
            setPublishedPeriods([]);
            return;
        }
        let cancelled = false;
        axios
            .get(`${getApiBaseUrl()}/ClassResults/periods/${effectiveClassId}`)
            .then(({ data }) => {
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                setPublishedPeriods(list);
                if (list.length > 0) {
                    const latest = list[0];
                    setRecordTerm(latest.term || "Term 1");
                    setRecordYear(String(latest.academicYear || new Date().getFullYear()));
                    setRecordPeriod(latest.reportPeriod || "End of term");
                }
            })
            .catch(() => {
                if (!cancelled) setPublishedPeriods([]);
            });
        return () => {
            cancelled = true;
        };
    }, [recordsMode, effectiveClassId]);

    const periodOptions = useMemo(
        () => allowedReportPeriods(classData?.educationLevel),
        [classData?.educationLevel],
    );

    useEffect(() => {
        if (!classData?.id && !classData?._id) return;
        let cancelled = false;

        const loadResults = async () => {
            setResultsLoading(true);
            try {
                if (isUniversity) {
                    const { data } = await axios.get(
                        `${getApiBaseUrl()}/semester/${id}?academicYear=${encodeURIComponent(academicYear)}&semester=${encodeURIComponent(semester)}`
                    );
                    if (cancelled) return;
                    const baseRows = Array.isArray(data) ? data : [];

                    const enriched = await Promise.all(
                        baseRows.map(async (row) => {
                            try {
                                const [studentRes, transcriptRes] = await Promise.all([
                                    axios.get(`${getApiBaseUrl()}/Student/${row.studentId}`),
                                    axios.get(`${getApiBaseUrl()}/transcript/${row.studentId}`),
                                ]);

                                const student = studentRes.data;
                                const semesterGroup = (transcriptRes.data?.grades_by_semester || []).find(
                                    (group) =>
                                        String(group.academicYear) === String(academicYear) &&
                                        String(group.semester) === String(semester)
                                );
                                const credits = (semesterGroup?.grades || []).reduce(
                                    (sum, grade) => sum + (Number(grade.creditHours) || Number(grade.subject?.creditHours) || 0),
                                    0
                                );

                                return {
                                    ...row,
                                    programme: student?.programme || "",
                                    credits,
                                };
                            } catch {
                                return {
                                    ...row,
                                    programme: row.programme || "",
                                    credits: row.credits || 0,
                                };
                            }
                        })
                    );

                    if (!cancelled) setUniversityResults(enriched);
                } else {
                    const params = new URLSearchParams();
                    if (recordsMode) {
                        params.set("publishedOnly", "true");
                        params.set("archivedRecordsOnly", "true");
                        params.set("term", recordTerm);
                        params.set("academicYear", recordYear);
                        params.set("reportPeriod", recordPeriod);
                    }
                    const qs = params.toString();
                    const resultsUrl = `${getApiBaseUrl()}/ClassResults/${effectiveClassId}${qs ? `?${qs}` : ""}`;
                    const requests = [axios.get(resultsUrl)];
                    if (!recordsMode) {
                        requests.push(axios.get(`${getApiBaseUrl()}/ExamLeaderboard/${effectiveClassId}`));
                    }
                    const responses = await Promise.all(requests);
                    if (!cancelled) {
                        setPrimaryResults(Array.isArray(responses[0].data) ? responses[0].data : []);
                        setLeaderboard(
                            recordsMode ? [] : Array.isArray(responses[1]?.data) ? responses[1].data : [],
                        );
                    }
                }
                if (!cancelled) setErrorMessage("");
            } catch (err) {
                if (!cancelled) {
                    setErrorMessage(err.response?.data?.message || "Could not load class results");
                    if (isUniversity) {
                        setUniversityResults([]);
                    } else {
                        setPrimaryResults([]);
                    }
                }
            } finally {
                if (!cancelled) setResultsLoading(false);
            }
        };

        loadResults();
        return () => {
            cancelled = true;
        };
    }, [classData, effectiveClassId, id, isUniversity, academicYear, semester, recordsMode, recordTerm, recordYear, recordPeriod]);

    const subjectColumns = useMemo(() => {
        const map = new Map();
        primaryResults.forEach((row) => {
            (row.subjects || []).forEach((subject) => {
                const key = subject.subjectId || subject.subjectName;
                if (!map.has(key)) {
                    map.set(key, {
                        key,
                        name: subject.subjectName || "Subject",
                    });
                }
            });
        });
        return Array.from(map.values());
    }, [primaryResults]);

    const primarySummary = useMemo(() => {
        const totalStudents = primaryResults.length;
        const average = totalStudents
            ? primaryResults.reduce((sum, row) => sum + (Number(row.averageMark) || 0), 0) / totalStudents
            : 0;
        const passedCount = primaryResults.filter((row) =>
            getMalawiGrade(
                row.averageMark,
                educationLevel,
                classData?.levelCode,
                classData?.sclassName
            ).passed
        ).length;
        const passRate = totalStudents ? (passedCount / totalStudents) * 100 : 0;
        const topStudent = primaryResults.find((row) => Number(row.position) === 1)?.name || "—";
        return {
            totalStudents,
            average,
            passRate,
            topStudent,
        };
    }, [primaryResults, educationLevel, classData?.levelCode, classData?.sclassName]);

    const universitySummary = useMemo(() => {
        const totalStudents = universityResults.length;
        const average = totalStudents
            ? universityResults.reduce((sum, row) => sum + (Number(row.GPA) || 0), 0) / totalStudents
            : 0;
        const passedCount = universityResults.filter((row) => (Number(row.GPA) || 0) >= 2.0).length;
        const passRate = totalStudents ? (passedCount / totalStudents) * 100 : 0;
        const topStudent = universityResults.find((row) => Number(row.position) === 1)?.name || "—";
        return {
            totalStudents,
            average,
            passRate,
            topStudent,
        };
    }, [universityResults]);

    const summary = isUniversity ? universitySummary : primarySummary;

    const primaryChartData = useMemo(() => {
        return subjectColumns.map((column) => {
            let total = 0;
            let count = 0;
            primaryResults.forEach((row) => {
                const item = (row.subjects || []).find((subject) => String(subject.subjectId || subject.subjectName) === String(column.key));
                if (item) {
                    total += Number(item.marksObtained) || 0;
                    count += 1;
                }
            });
            return {
                subject: column.name,
                average: count ? Number((total / count).toFixed(2)) : 0,
            };
        });
    }, [primaryResults, subjectColumns]);

    const universityChartData = useMemo(() => {
        const bands = { A: 0, B: 0, C: 0, D: 0, F: 0 };
        universityResults.forEach((row) => {
            bands[gpaBandLetter(row.GPA)] += 1;
        });
        return Object.entries(bands).map(([grade, count]) => ({ grade, count }));
    }, [universityResults]);

    const exportPrimaryCsv = () => {
        const today = new Date().toISOString().slice(0, 10);
        const safeClassName = String(classData?.sclassName || "class-results").replace(/[^\w-]+/g, "-");
        const headers = ["Position", "Student Name", "Roll No", ...subjectColumns.map((c) => c.name), "Average", "Overall Grade", "Status"];
        const rows = primaryResults.map((row) => {
            const overall = getMalawiGrade(
                row.averageMark,
                educationLevel,
                classData?.levelCode,
                classData?.sclassName
            );
            const subjectCells = subjectColumns.map((column) => {
                const item = (row.subjects || []).find((subject) => String(subject.subjectId || subject.subjectName) === String(column.key));
                return item ? `${item.marksObtained} / ${item.grade}` : "";
            });
            return [
                row.position,
                row.name,
                row.rollNum,
                ...subjectCells,
                Number(row.averageMark || 0).toFixed(2),
                overall.grade,
                overall.passed ? "Pass" : "Fail",
            ];
        });
        exportCsv(`class-results-${safeClassName}-${today}.csv`, [headers, ...rows]);
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="xl">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                    <Box>
                        {initialLoading ? (
                            <>
                                <Skeleton width={220} height={36} />
                                <Skeleton width={180} height={24} />
                            </>
                        ) : (
                            <>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                                    <Typography variant="h5" fontWeight={700}>
                                        {classData?.sclassName || "Class Results"}
                                    </Typography>
                                    {recordsMode ? (
                                        <Chip label="Published records" color="success" size="small" />
                                    ) : null}
                                    <Chip label={educationLevel} color="primary" size="small" />
                                    {classData?.isExamClass ? (
                                        <Chip label={classData.examType || "Exam Class"} color="warning" size="small" />
                                    ) : null}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {recordsMode
                                        ? "Official marks archive for this class. Only director-approved results appear here."
                                        : `Results overview for ${isUniversity ? "semester GPA performance" : "MANEB class performance"}.`}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {recordsMode ? (
                            <Button
                                variant="text"
                                component={RouterLink}
                                to="/Admin/exams/records"
                            >
                                All records
                            </Button>
                        ) : (
                            <Button variant="text" onClick={() => navigate("/Admin/classes/results/overview")}>
                                All classes
                            </Button>
                        )}
                        {!isUniversity && (
                            <Button
                                variant="outlined"
                                startIcon={<DownloadOutlinedIcon />}
                                onClick={exportPrimaryCsv}
                                disabled={resultsLoading || !primaryResults.length}
                            >
                                Export CSV
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {recordsMode && !isUniversity ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                        {publishedPeriods.length === 0 && !initialLoading ? (
                            <Alert severity="info" sx={{ mb: 0 }}>
                                No published record for this class yet. Approve a package under{" "}
                                <strong>Approve class results</strong>.
                            </Alert>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Term</InputLabel>
                                        <Select
                                            label="Term"
                                            value={recordTerm}
                                            onChange={(e) => setRecordTerm(e.target.value)}
                                        >
                                            {TERM_OPTIONS.map((t) => (
                                                <MenuItem key={t} value={t}>
                                                    {t}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            label="Year"
                                            value={recordYear}
                                            onChange={(e) => setRecordYear(e.target.value)}
                                        >
                                            {[0, 1, 2].map((off) => {
                                                const y = String(new Date().getFullYear() - off);
                                                return (
                                                    <MenuItem key={y} value={y}>
                                                        {y}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Report period</InputLabel>
                                        <Select
                                            label="Report period"
                                            value={recordPeriod}
                                            onChange={(e) => setRecordPeriod(e.target.value)}
                                        >
                                            {periodOptions.map((p) => (
                                                <MenuItem key={p} value={p}>
                                                    {p}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Published set</InputLabel>
                                        <Select
                                            label="Published set"
                                            value={
                                                publishedPeriods.findIndex(
                                                    (p) =>
                                                        p.term === recordTerm &&
                                                        String(p.academicYear) === String(recordYear) &&
                                                        p.reportPeriod === recordPeriod,
                                                ) >= 0
                                                    ? String(
                                                          publishedPeriods.findIndex(
                                                              (p) =>
                                                                  p.term === recordTerm &&
                                                                  String(p.academicYear) ===
                                                                      String(recordYear) &&
                                                                  p.reportPeriod === recordPeriod,
                                                          ),
                                                      )
                                                    : ""
                                            }
                                            onChange={(e) => {
                                                const idx = Number(e.target.value);
                                                const row = publishedPeriods[idx];
                                                if (!row) return;
                                                setRecordTerm(row.term || "Term 1");
                                                setRecordYear(String(row.academicYear || recordYear));
                                                setRecordPeriod(row.reportPeriod || "End of term");
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Custom filters</em>
                                            </MenuItem>
                                            {publishedPeriods.map((p, idx) => (
                                                <MenuItem key={p.id || p._id || idx} value={String(idx)}>
                                                    {[p.term, p.reportPeriod, p.academicYear]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                ) : null}

                {isUniversity && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Academic Year</InputLabel>
                                <Select label="Academic Year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                    {academicYearOptions.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Semester</InputLabel>
                                <Select label="Semester" value={semester} onChange={(e) => setSemester(e.target.value)}>
                                    {SEMESTERS.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Paper>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <SharedKpiCard
                            title="Total Students"
                            value={summary.totalStudents}
                            icon={PeopleAltOutlinedIcon}
                            color="primary"
                            loading={initialLoading || resultsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SharedKpiCard
                            title="Average"
                            value={isUniversity ? summary.average.toFixed(2) : `${summary.average.toFixed(2)}%`}
                            icon={AssessmentOutlinedIcon}
                            color="info"
                            loading={initialLoading || resultsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SharedKpiCard
                            title="Pass Rate"
                            value={`${summary.passRate.toFixed(1)}%`}
                            icon={CheckCircleOutlineOutlinedIcon}
                            color="success"
                            loading={initialLoading || resultsLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SharedKpiCard
                            title="Top Student"
                            value={summary.topStudent}
                            icon={EmojiEventsOutlinedIcon}
                            color="warning"
                            loading={initialLoading || resultsLoading}
                        />
                    </Grid>
                </Grid>

                {errorMessage ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        {isUniversity ? "University Semester Results" : "Class Results"}
                    </Typography>

                    {resultsLoading ? (
                        <Skeleton variant="rounded" width="100%" height={320} />
                    ) : !isUniversity ? (
                        <ResponsiveTableWrap>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Position</TableCell>
                                        <TableCell>Student Name</TableCell>
                                        <TableCell>Roll No</TableCell>
                                        {subjectColumns.map((column) => (
                                            <TableCell key={column.key}>{column.name}</TableCell>
                                        ))}
                                        <TableCell align="right">Average</TableCell>
                                        <TableCell>Overall Grade</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Report</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {primaryResults.map((row) => {
                                        const overall = getMalawiGrade(
                row.averageMark,
                educationLevel,
                classData?.levelCode,
                classData?.sclassName
            );
                                        return (
                                            <TableRow key={row.studentId} hover>
                                                <TableCell>{row.position}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.rollNum}</TableCell>
                                                {subjectColumns.map((column) => {
                                                    const subject = (row.subjects || []).find(
                                                        (item) => String(item.subjectId || item.subjectName) === String(column.key)
                                                    );
                                                    return (
                                                        <TableCell key={`${row.studentId}-${column.key}`}>
                                                            {subject ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color:
                                                                            subject.grade === "1" || subject.grade === "2"
                                                                                ? "success.main"
                                                                                : ["3", "4", "5"].includes(subject.grade)
                                                                                ? "info.main"
                                                                                : subject.grade === "6"
                                                                                ? "warning.main"
                                                                                : "error.main",
                                                                    }}
                                                                >
                                                                    {subject.marksObtained} / {subject.grade}
                                                                </Typography>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell align="right">{Number(row.averageMark || 0).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Chip label={`Grade ${overall.grade}`} size="small" sx={{ fontWeight: 700 }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={overall.passed ? "Pass" : "Fail"}
                                                        size="small"
                                                        color={overall.passed ? "success" : "error"}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={<ArticleOutlinedIcon />}
                                                        onClick={() =>
                                                            navigate(`/Admin/students/report-card/${row.studentId}`)
                                                        }
                                                    >
                                                        Card
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </ResponsiveTableWrap>
                    ) : (
                        <ResponsiveTableWrap>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Position</TableCell>
                                        <TableCell>Reg Number</TableCell>
                                        <TableCell>Student Name</TableCell>
                                        <TableCell>Programme</TableCell>
                                        <TableCell align="right">Credits</TableCell>
                                        <TableCell align="right">GPA</TableCell>
                                        <TableCell>Classification</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Transcript</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {universityResults.map((row) => {
                                        const style = getClassificationColor(row.classification);
                                        return (
                                            <TableRow key={row.studentId} hover>
                                                <TableCell>{row.position}</TableCell>
                                                <TableCell>{row.regNumber || "—"}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.programme || "—"}</TableCell>
                                                <TableCell align="right">{row.credits || 0}</TableCell>
                                                <TableCell align="right">{Number(row.GPA || 0).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.classification}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: style.bg,
                                                            color: style.color,
                                                            borderColor: style.border,
                                                        }}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={(Number(row.GPA) || 0) >= 2.0 ? "Pass" : "Fail"}
                                                        size="small"
                                                        color={(Number(row.GPA) || 0) >= 2.0 ? "success" : "error"}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={<ArticleOutlinedIcon />}
                                                        onClick={() => navigate(`/Admin/students/transcript/${row.studentId}`)}
                                                    >
                                                        Transcript
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </ResponsiveTableWrap>
                    )}
                </Paper>

                {!isUniversity && leaderboard.length > 0 ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                            Top 10 leaderboard
                        </Typography>
                        <ResponsiveTableWrap>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Rank</TableCell>
                                        <TableCell>Student</TableCell>
                                        <TableCell align="right">Average</TableCell>
                                        <TableCell>Grade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {leaderboard.map((row) => {
                                        const g = getMalawiGrade(
                                            row.averageMark,
                                            educationLevel,
                                            classData?.levelCode,
                                            classData?.sclassName
                                        );
                                        return (
                                            <TableRow key={row.studentId}>
                                                <TableCell>{row.position}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell align="right">{Number(row.averageMark || 0).toFixed(1)}%</TableCell>
                                                <TableCell>
                                                    <Chip label={g.grade} size="small" color={g.passed ? "success" : "error"} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </ResponsiveTableWrap>
                    </Paper>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        {isUniversity ? "GPA Distribution" : "Average Marks by Subject"}
                    </Typography>
                    {resultsLoading ? (
                        <Skeleton variant="rounded" width="100%" height={320} />
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            {!isUniversity ? (
                                <BarChart data={primaryChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="subject" interval={0} angle={primaryChartData.length > 6 ? -20 : 0} textAnchor={primaryChartData.length > 6 ? "end" : "middle"} height={primaryChartData.length > 6 ? 70 : 40} />
                                    <YAxis domain={[0, 100]} />
                                    <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
                                    <Bar dataKey="average" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            ) : (
                                <BarChart data={universityChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="grade" />
                                    <YAxis allowDecimals={false} />
                                    <RechartsTooltip formatter={(value) => `${value} students`} />
                                    <Bar dataKey="count" fill="#7b1fa2" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            )}
                        </ResponsiveContainer>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ClassResults;
